"use client"

import { useState, useEffect } from "react"
import { X, Upload, Plus, Trash2, Package, Tag } from "lucide-react"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Badge } from "./ui/badge"
import { toast } from "react-toastify"
import uploadImage from "../helpers/uploadImage"
import summaryApi from "../common/index"



const AdminEditProduct = ({ productData, onClose, fetchData }) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [data, setData] = useState({
    productName: "",
    priceRs: "",
    compareAtPriceRs: "",
    category: "dates",
    customCategory: "",
    brandName: "Saudi Dates",
    brandLogo: { url: "", publicId: "" },
    variety: "",
    origin: "Madinah Al Munawrah, Saudi Arabia",
    productImages: [],
    description: [],
    packagingOptions: [],
    tags: [],
    materials: [],
    careInstructions: [],
    itemNumber: "",
  })


  
  const [tempInputs, setTempInputs] = useState({
    descriptionItem: "",
    tagItem: "",
    materialItem: "",
    careItem: "",
    packagingInput: { weight: "", stock: "", sku: "" },
  })

  const [uploadingImage, setUploadingImage] = useState(false)
  const [uploadingBrandLogo, setUploadingBrandLogo] = useState(false)

  const categoryOptions = [
    { value: "dates", label: "Dates" },
    { value: "dry-fruits", label: "Dry Fruits" },
    { value: "attar", label: "Attar" },
    { value: "other", label: "Other" },
  ]

 useEffect(() => {
  if (productData) {
    const predefinedCategories = ["dates", "dry-fruits", "attar"];
    const isPredefined = predefinedCategories.includes(productData.category);

    setData({
      _id: productData._id,
      productName: productData.productName || "",
      priceRs: productData.priceRs?.toString() || "",
      compareAtPriceRs: productData.compareAtPriceRs?.toString() || "",
      category: isPredefined ? productData.category : "other", // "other" if custom
      customCategory: isPredefined ? "" : productData.category, // save original if custom
      brandName: productData.brandName || "Saudi Dates",
      brandLogo: productData.brandLogo || { url: "", publicId: "" },
      variety: productData.variety || "",
      origin: productData.origin || "Madinah Al Munawrah, Saudi Arabia",
      productImages: productData.productImages || [],
      description: Array.isArray(productData.description)
        ? productData.description
        : productData.description
          ? [productData.description]
          : [],
      packagingOptions: productData.packagingOptions || [],
      tags: Array.isArray(productData.tags)
        ? productData.tags
        : productData.tags
          ? [productData.tags]
          : [],
      materials: productData.materials || [],
      careInstructions: productData.careInstructions || [],
      itemNumber: productData.itemNumber || "",
    });
  }
}, [productData]);


  const handleOnChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".")
      setData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }))
    } else {
      setData((prev) => ({
        ...prev,
        [field]: value,
      }))
    }
  }

  const handleTempInputChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".")
      setTempInputs((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }))
    } else {
      setTempInputs((prev) => ({
        ...prev,
        [field]: value,
      }))
    }
  }

  const addArrayItem = (arrayName, item) => {
    if (!item || (typeof item === "string" && !item.trim())) return

    setData((prev) => ({
      ...prev,
      [arrayName]: [...prev[arrayName], item],
    }))

    if (arrayName === "packagingOptions") {
      setTempInputs((prev) => ({
        ...prev,
        packagingInput: { weight: "", stock: "", sku: "" },
      }))
    } else {
      const tempField =
        arrayName === "description"
          ? "descriptionItem"
          : arrayName === "tags"
            ? "tagItem"
            : arrayName === "materials"
              ? "materialItem"
              : arrayName === "careInstructions"
                ? "careItem"
                : ""

      setTempInputs((prev) => ({
        ...prev,
        [tempField]: "",
      }))
    }
  }

  const removeArrayItem = (arrayName, index) => {
    setData((prev) => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index),
    }))
  }

  const handleUploadImage = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    setUploadingImage(true)
    try {
      const imageUrl = await uploadImage(file)
      if (imageUrl?.secure_url) {
        const newImage = {
          url: imageUrl.secure_url,
          publicId: imageUrl.public_id,
          alt: data.productName || "Product image",
          isPrimary: data.productImages.length === 0,
        }

        setData((prev) => ({
          ...prev,
          productImages: [...prev.productImages, newImage],
        }))
        toast.success("Image uploaded successfully!")
      } else {
        toast.error("Image upload failed")
      }
    } catch (error) {
      console.error("Error uploading image:", error)
      toast.error("Image upload failed")
    } finally {
      setUploadingImage(false)
    }
  }

  const handleUploadBrandLogo = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    setUploadingBrandLogo(true)
    try {
      const imageUrl = await uploadImage(file)
      if (imageUrl?.secure_url) {
        setData((prev) => ({
          ...prev,
          brandLogo: {
            url: imageUrl.secure_url,
            publicId: imageUrl.public_id,
          },
        }))
        toast.success("Brand logo uploaded successfully!")
      } else {
        toast.error("Brand logo upload failed")
      }
    } catch (error) {
      console.error("Error uploading brand logo:", error)
      toast.error("Brand logo upload failed")
    } finally {
      setUploadingBrandLogo(false)
    }
  }

  const handleDeleteImage = (index) => {
    setData((prev) => ({
      ...prev,
      productImages: prev.productImages.filter((_, i) => i !== index),
    }))
  }

  const setPrimaryImage = (index) => {
    setData((prev) => ({
      ...prev,
      productImages: prev.productImages.map((img, i) => ({
        ...img,
        isPrimary: i === index,
      })),
    }))
  }

 const handleSubmit = async (e) => {
  e.preventDefault()

  if (!data._id) {
    toast.error("Product ID is missing!")
    return
  }

  // Prepare data for submission
  const submitData = { ...data }

  // If category is "other" and a custom category is provided, use it
  if (submitData.category === "other" && submitData.customCategory) {
    submitData.category = submitData.customCategory
    submitData.customCategory = null // optional: clear customCategory
  }

  const response = await fetch(summaryApi.updateProduct.url, {
    method: summaryApi.updateProduct.method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(submitData),
  })

  const responseData = await response.json()
  console.log("Response Data:", responseData)

  if (responseData.success) {
    toast.success(responseData.message, {
      style: {
        background: "linear-gradient(to right, #4a90e2, #9013fe)",
        color: "white",
      },
      autoClose: 3000,
      closeOnClick: true,
    })
    onClose()
    fetchData()
  }

  if (responseData.error) {
    toast.error(responseData.message, {
      style: {
        background: "linear-gradient(to right, #e94e77, #ff6b6b)",
        color: "white",
      },
      autoClose: 3000,
      closeOnClick: true,
    })
  }
}


  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
    }
  }

  const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, 4))
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1))

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6 bg-white">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary">
                  <Package className="h-5 w-5" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="productName">Product Name *</Label>
                  <Input
                    id="productName"
                    value={data.productName}
                    onChange={(e) => handleOnChange("productName", e.target.value)}
                    placeholder="e.g. Ajwa Dates, Medjool, Attar Mukhallat"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="priceRs">Price (£) *</Label>
                    <Input
                      id="priceRs"
                      type="number"
                      value={data.priceRs}
                      onChange={(e) => handleOnChange("priceRs", e.target.value)}
                      placeholder="0"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="compareAtPriceRs">Compare at Price (£.)</Label>
                    <Input
                      id="compareAtPriceRs"
                      type="number"
                      value={data.compareAtPriceRs}
                      onChange={(e) => handleOnChange("compareAtPriceRs", e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="variety">Variety</Label>
                  <Input
                    id="variety"
                    value={data.variety}
                    onChange={(e) => handleOnChange("variety", e.target.value)}
                    placeholder="e.g. Ajwa, Medjool, Aseel, Mabroom"
                  />
                </div>

                <div>
                  <Label htmlFor="origin">Origin</Label>
                  <Input
                    id="origin"
                    value={data.origin}
                    onChange={(e) => handleOnChange("origin", e.target.value)}
                    placeholder="e.g. Madinah Al Munawarah, Saudi Arabia"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )

  case 2:
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-primary">
            <Tag className="h-5 w-5" />
            Category & Brand
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Category Selection */}
          <div>
            <Label>Category *</Label>
            <Select
              value={data.category}
              onValueChange={(value) => handleOnChange("category", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Category Input (only if "other" is selected) */}
          {data.category === "other" && (
            <div>
              <Label htmlFor="customCategory">Custom Category</Label>
              <Input
                id="customCategory"
                value={data.customCategory || ""}
                onChange={(e) => handleOnChange("customCategory", e.target.value)}
                placeholder="Enter custom category"
              />
            </div>
          )}

          {/* Brand Name */}
          <div>
            <Label htmlFor="brandName">Brand Name</Label>
            <Input
              id="brandName"
              value={data.brandName}
              onChange={(e) => handleOnChange("brandName", e.target.value)}
              placeholder="Enter brand name"
            />
          </div>

          {/* Brand Logo Upload */}
          <div>
            <Label>Brand Logo</Label>
            <div className="flex items-center gap-4">
              {data.brandLogo?.url && (
                <img
                  src={data.brandLogo.url || "/placeholder.svg"}
                  alt="Brand logo"
                  className="w-16 h-16 object-cover rounded border"
                />
              )}
              <div className="border-2 border-dashed border-border rounded-lg p-4 text-center flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadBrandLogo}
                  className="hidden"
                  id="brandLogoUpload"
                  disabled={uploadingBrandLogo}
                />
                <label htmlFor="brandLogoUpload" className="cursor-pointer">
                  <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {uploadingBrandLogo ? "Uploading..." : "Click to upload brand logo"}
                  </p>
                </label>
              </div>
            </div>
          </div>

        </CardContent>
      </Card>
    </div>
  );


      case 3:
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary">
                  <Upload className="h-5 w-5" />
                  Images & Description
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Product Images</Label>
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleUploadImage}
                      className="hidden"
                      id="imageUpload"
                      disabled={uploadingImage}
                    />
                    <label htmlFor="imageUpload" className="cursor-pointer">
                      <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        {uploadingImage ? "Uploading..." : "Click to upload images"}
                      </p>
                    </label>
                  </div>

                  {data.productImages.length > 0 && (
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      {data.productImages.map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={image.url || "/placeholder.svg"}
                            alt={image.alt}
                            className="w-full h-24 object-cover rounded-lg border"
                          />
                          {image.isPrimary && <Badge className="absolute top-1 left-1 text-xs">Primary</Badge>}
                          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteImage(index)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          {!image.isPrimary && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="absolute bottom-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity text-xs bg-white"
                              onClick={() => setPrimaryImage(index)}
                            >
                              Set Primary
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label>Description Points</Label>
                  <div className="flex gap-2">
                    <Input
                      value={tempInputs.descriptionItem}
                      onChange={(e) => handleTempInputChange("descriptionItem", e.target.value)}
                      placeholder="Add description point"
                      onKeyDown={(e) => e.key === "Enter" && addArrayItem("description", tempInputs.descriptionItem)}
                    />
                    <Button
                      type="button"
                      onClick={() => addArrayItem("description", tempInputs.descriptionItem)}
                      size="sm"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {data.description.map((item, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1">
                        {item}
                        <button
                          type="button"
                          onClick={() => removeArrayItem("description", index)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Tags</Label>
                  <div className="flex gap-2">
                    <Input
                      value={tempInputs.tagItem}
                      onChange={(e) => handleTempInputChange("tagItem", e.target.value)}
                      placeholder="Add tag"
                      onKeyDown={(e) => e.key === "Enter" && addArrayItem("tags", tempInputs.tagItem)}
                    />
                    <Button type="button" onClick={() => addArrayItem("tags", tempInputs.tagItem)} size="sm">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {data.tags.map((item, index) => (
                      <Badge key={index} variant="outline" className="flex items-center gap-1">
                        {item}
                        <button
                          type="button"
                          onClick={() => removeArrayItem("tags", index)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )

      case 4:
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary">
                  <Package className="h-5 w-5" />
                  Packaging Options
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Add Packaging Option</Label>
                  <div className="grid grid-cols-4 gap-2">
                    <Input
                      value={tempInputs.packagingInput.weight}
                      onChange={(e) => handleTempInputChange("packagingInput.weight", e.target.value)}
                      placeholder="Weight (e.g. 250g)"
                    />
                    <Input
                      type="number"
                      value={tempInputs.packagingInput.stock}
                      onChange={(e) => handleTempInputChange("packagingInput.stock", e.target.value)}
                      placeholder="Stock"
                    />
                    <Input
                      value={tempInputs.packagingInput.sku}
                      onChange={(e) => handleTempInputChange("packagingInput.sku", e.target.value)}
                      placeholder="SKU"
                    />
                    <Button
                      type="button"
                      onClick={() =>
                        addArrayItem("packagingOptions", {
                          weight: tempInputs.packagingInput.weight,
                          stock: Number.parseInt(tempInputs.packagingInput.stock) || 0,
                          sku: tempInputs.packagingInput.sku,
                        })
                      }
                      size="sm"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {data.packagingOptions.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {data.packagingOptions.map((opt, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                          <span>
                            {opt.weight} - Stock: {opt.stock} - SKU: {opt.sku || "N/A"}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeArrayItem("packagingOptions", index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label>Materials</Label>
                  <div className="flex gap-2">
                    <Input
                      value={tempInputs.materialItem}
                      onChange={(e) => handleTempInputChange("materialItem", e.target.value)}
                      placeholder="Add material"
                      onKeyDown={(e) => e.key === "Enter" && addArrayItem("materials", tempInputs.materialItem)}
                    />
                    <Button type="button" onClick={() => addArrayItem("materials", tempInputs.materialItem)} size="sm">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {data.materials.map((item, index) => (
                      <Badge key={index} variant="outline" className="flex items-center gap-1">
                        {item}
                        <button
                          type="button"
                          onClick={() => removeArrayItem("materials", index)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Care Instructions</Label>
                  <div className="flex gap-2">
                    <Input
                      value={tempInputs.careItem}
                      onChange={(e) => handleTempInputChange("careItem", e.target.value)}
                      placeholder="Add care instruction"
                      onKeyDown={(e) => e.key === "Enter" && addArrayItem("careInstructions", tempInputs.careItem)}
                    />
                    <Button
                      type="button"
                      onClick={() => addArrayItem("careInstructions", tempInputs.careItem)}
                      size="sm"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {data.careInstructions.map((item, index) => (
                      <Badge key={index} variant="outline" className="flex items-center gap-1">
                        {item}
                        <button
                          type="button"
                          onClick={() => removeArrayItem("careInstructions", index)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b bg-white shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-primary">Edit Product</h2>
            <p className="text-sm text-muted-foreground">Step {currentStep} of 4</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onKeyDown={handleKeyDown} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-6">{renderStepContent()}</div>

          <div className="flex items-center justify-between p-6 border-t bg-muted/30 shrink-0">
            <Button type="button" variant="outline" onClick={prevStep} disabled={currentStep === 1}>
              Previous
            </Button>

            <div className="flex gap-2">
              {[1, 2, 3, 4].map((step) => (
                <div
                  key={step}
                  className={`w-2 h-2 rounded-full ${
                    step === currentStep
                      ? "bg-primary"
                      : step < currentStep
                        ? "bg-primary/60"
                        : "bg-muted-foreground/30"
                  }`}
                />
              ))}
            </div>

            {currentStep < 4 ? (
              <Button type="button" onClick={nextStep}>
                Next
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                className="bg-black text-white border border-white hover:bg-gray-800 hover:border-gray-800"
              >
                Update Product
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default AdminEditProduct
